import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IDecaDocument, isDecaServicePriorToTimestamp, normalizePlateNumber, UUID } from '@okcargo-apps/shared-domain';
import { DecaEditReasonComponent } from '../deca-edit-reason/deca-edit-reason.component';
import { DecaEditPlatesForm, DecaEditReasonFormModel } from '../deca-forms.model';
import { DecaListItemComponent } from '../deca-list-item/deca-list-item.component';
import { DecaMutationsService } from '../deca-mutations.service';
import { DecaStoreService } from '../deca-store.service';

@Component({
  selector: 'lib-deca-edit-plates-modal',
  templateUrl: './deca-edit-plates-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonCol,
    IonRow,
    IonGrid,
    IonFooter,
    ReactiveFormsModule,
    IonContent,
    IonInput,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    DecaEditReasonComponent,
    DecaListItemComponent,
  ],
})
export class DecaEditPlatesModalComponent implements OnInit {
  // comes from the component that opens the modal
  public readonly document!: IDecaDocument;
  public readonly userCompanyId!: UUID;

  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly decaMutationsService = inject(DecaMutationsService);
  private readonly decaStoreService = inject(DecaStoreService);
  private readonly messagesUiService = inject(MessagesUiService);

  protected readonly form: FormGroup<DecaEditPlatesForm> = this.fb.group<DecaEditPlatesForm>({
    tractorPlateNumber: this.fb.nonNullable.control(''),
    trailerPlateNumber: this.fb.nonNullable.control(''),
    changeReason: this.fb.group<DecaEditReasonFormModel>({ reason: this.fb.nonNullable.control('') }),
  });

  protected get isServiceStarted(): boolean {
    return isDecaServicePriorToTimestamp(this.document, new Date());
  }

  private requireChangeReasonWhenServiceStarted(): void {
    if (!this.isServiceStarted) return;

    const reason = this.form.controls.changeReason.controls.reason;
    reason.addValidators(Validators.required);
    reason.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.requireChangeReasonWhenServiceStarted();
    this.form.patchValue({
      tractorPlateNumber: this.document.tractorPlateNumber ?? '',
      trailerPlateNumber: this.document.trailerPlateNumber ?? '',
    });
  }

  protected async saveChanges(): Promise<void> {
    if (this.form.invalid) {
      markFormAsInvalid(this.form, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: 'Enviando datos...',
    });
    await loading.present();

    try {
      const updatedDocument: IDecaDocument = {
        ...this.document,
        tractorPlateNumber: normalizePlateNumber(this.form.controls.tractorPlateNumber.value),
        trailerPlateNumber: normalizePlateNumber(this.form.controls.trailerPlateNumber.value),
      };

      await this.decaMutationsService.updatePlateNumbers(
        updatedDocument,
        this.form.controls.changeReason.controls.reason.value.trim(),
      );

      this.messagesUiService.setMessage({
        objectId: updatedDocument.id,
        message: ['Ha enviado cambios en las matrículas para este documento'],
        durationMs: 3000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));

      this.decaStoreService.reloadAfterSaveData();

      this.modalCtrl.dismiss(updatedDocument, 'confirm');
    } catch (error) {
      // TODO manage error
      console.error('Error updating plates', error);
    } finally {
      await loading.dismiss();
    }
  }

  protected closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
